import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.analysis');
import { OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Service } from "src/libs/portal/season/service";
import {
    cellyticsAnalysisSet,
    cellyticsCurrentAnalysisSetId,
    cellyticsDefaultAnalysisSet,
} from '../cellytics-analysis';

const ACCESS_SESSION_KEY = "cellytics.access";
const ACCESS_SESSION_VALUE = "granted";
const ACCESS_USER = {
    id: "cellytics-access",
    email: "",
    name: "Cellytics NK",
    role: "admin",
};
const RAW_ANALYSIS_URL = "/assets/cellytics/260622/raw-analysis/analysis-data.json";

@Component({
    selector: 'wiz-page-analysis',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.analysis/view.scss */
:host {
  display: block;
  min-height: 100%;
  background: #f8fafc;
}`],
})
export class PageAnalysisComponent implements OnInit, OnDestroy {
    public data: any = null;
    public analysisSetId: string = "";
    public analysisSet: any = null;
    public loading: boolean = true;
    public error: string = "";
    public selectedDatasetId: string = "all";
    public classLabels: string[] = ["ASC", "vehicle"];

    private routerSub: any = null;

    constructor(@Inject( Service) public service: Service,@Inject( Router)  private router: Router) { }

    public async ngOnInit() {
        await this.service.init();
        if (!this.hasAccess()) {
            location.href = "/access";
            return;
        }
        this.applyAccessSession();
        this.syncAnalysisSet();
        this.bindRouteChanges();
        await this.load();
    }

    public ngOnDestroy() {
        if (this.routerSub) this.routerSub.unsubscribe();
    }

    private hasAccess() {
        return this.service.auth.check() || window.sessionStorage.getItem(ACCESS_SESSION_KEY) === ACCESS_SESSION_VALUE;
    }

    private applyAccessSession() {
        if (this.service.auth.check()) return;
        this.service.auth.status = true;
        this.service.auth.session = ACCESS_USER;
    }

    private syncAnalysisSet() {
        this.analysisSetId = cellyticsCurrentAnalysisSetId();
        this.analysisSet = cellyticsAnalysisSet(this.analysisSetId) || cellyticsDefaultAnalysisSet();
    }

    private bindRouteChanges() {
        if (this.routerSub) return;
        this.routerSub = this.router.events.subscribe(async (event: any) => {
            if (!(event instanceof NavigationEnd)) return;
            const nextSetId = cellyticsCurrentAnalysisSetId();
            if (nextSetId === this.analysisSetId) return;
            this.syncAnalysisSet();
            await this.load();
        });
    }

    public async load() {
        this.syncAnalysisSet();
        this.loading = true;
        this.error = "";
        await this.service.render();

        try {
            const response = await fetch(RAW_ANALYSIS_URL, { cache: "no-cache" });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            this.data = await response.json();
            this.selectedDatasetId = "all";
        } catch (error) {
            this.error = "원본 재분석 데이터를 불러오지 못했습니다.";
        }

        this.loading = false;
        await this.service.render();
    }

    public get is260622() {
        return this.analysisSetId === "260622";
    }

    public get summary() {
        return this.data?.summary || {};
    }

    public get samples() {
        return this.data?.samples || [];
    }

    public get datasetSummaries() {
        return this.summary?.datasetSummaries || [];
    }

    public get datasetOptions() {
        return [
            {
                datasetId: "all",
                label: "전체",
                sampleCount: this.summary.sampleCount,
                sourceImageCount: this.summary.sourceImageCount,
            },
            ...this.datasetSummaries,
        ];
    }

    public get visibleSamples() {
        if (this.selectedDatasetId === "all") return this.samples;
        return this.samples.filter((sample: any) => sample.datasetId === this.selectedDatasetId);
    }

    public get filterDescription() {
        const filter = this.data?.parameters?.csv;
        if (!filter) return "-";
        return `이미지 CSV별 CSP 하위 ${this.format(filter.cspLowerTailPercent, 1)}% + 상위 ${this.format(filter.cspUpperTailPercent, 1)}% 제거`;
    }

    public async selectDataset(datasetId: string) {
        this.selectedDatasetId = datasetId;
        await this.service.render();
    }

    public datasetButtonClass(datasetId: string) {
        if (datasetId === this.selectedDatasetId) {
            return "rounded-md bg-gray-950 px-3 py-1.5 text-[12px] font-semibold text-white";
        }
        return "rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-600 hover:bg-gray-50";
    }

    public sampleClass(sample: any, label: string) {
        return sample?.classes?.[label] || {
            label,
            imageCount: 0,
            rawCellCount: 0,
            filteredCellCount: 0,
            rawMax: null,
            filteredMax: null,
            rawOver1000Count: 0,
            over1000Count: 0,
            images: [],
            mitRows: [],
            cspResult: null,
        };
    }

    public csvRows(sample: any) {
        const urls = sample?.csvUrls || {};
        return Object.keys(urls).map((fileName: string) => ({
            fileName,
            label: this.csvLabel(fileName),
            url: urls[fileName],
        }));
    }

    public statusLabel(status: string) {
        const labels: any = {
            success: "CSV 완료",
            partial: "부분 결과",
            empty: "cell CSV 없음",
            not_run: "미실행",
            not_available: "없음",
        };
        return labels[status] || status || "-";
    }

    public statusClass(status: string) {
        if (status === "success") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
        if (status === "partial") return "bg-amber-50 text-amber-700 ring-amber-200";
        if (status === "empty") return "bg-gray-100 text-gray-600 ring-gray-200";
        return "bg-gray-50 text-gray-500 ring-gray-200";
    }

    public over1000Class(value: any) {
        if (Number(value) > 0) return "text-amber-700";
        return "text-emerald-700";
    }

    public csvLabel(fileName: string) {
        const labels: any = {
            "data_class_all.csv": "cell 전체 원본",
            "data_filter_class_all.csv": "cell CSP filter 후",
            "data_CSP_result.csv": "CSP 통계 원본",
            "data_filter_CSP_result.csv": "CSP 통계 filter 후",
            "data_class_MIT.csv": "MIT 통계 원본",
            "data_filter_class_MIT.csv": "MIT 통계 filter 후",
            "data_class_I3.csv": "I3 원본",
            "data_filter_I3.csv": "I3 filter 후",
            "data_summary_df.csv": "8 parameter 원본",
            "data_filter_summary_df.csv": "8 parameter filter 후",
        };
        return labels[fileName] || fileName;
    }

    public format(value: any, digits: number = 0) {
        if (value === null || value === undefined || value === "") return "-";
        const numberValue = Number(value);
        if (Number.isNaN(numberValue)) return String(value);
        return numberValue.toLocaleString("ko-KR", {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        });
    }

    public formatPercent(value: any, digits: number = 1) {
        const formatted = this.format(value, digits);
        if (formatted === "-") return formatted;
        return `${formatted}%`;
    }
}

export default PageAnalysisComponent;